// Vocaler domain model + API contracts.
// Shapes are derived from the FastAPI BFF at /api/v1/web/*. The BFF emits
// camelCase JSON via Pydantic alias generators, so these interfaces match the
// wire format exactly — no transformation layer required.

import type { DotState } from "../lib/brand";

/* ---------- Shared ---------- */
export type MediaKind =
  | "music"
  | "image"
  | "video"
  | "project"
  | "bundle"
  // Server-rendered visualizer composition (cover + music + canvas
  // overlay). UX-wise it's a video; the SPA buckets it into the Video
  // tab + uses the VideoThumb component for its card thumbnail.
  | "visualizer";
export type Aspect = "audio" | "9:16" | "16:9" | "1:1";
export type PublishStatus = "published" | "scheduled" | "partial" | "not_published" | "draft";

export interface PublishDestination {
  icon: string;
  platform: string;
  handle: string;
  time: string;
  ok?: boolean;
  failed?: boolean;
}
export interface PublishState {
  status: PublishStatus;
  label: string;
  destinations: PublishDestination[];
}

/* ---------- User / account ---------- */
export type Role = "owner" | "admin" | "member";
export interface User {
  id: string;
  name: string;
  initials: string;
  email: string;
  role?: Role;
  /** Platform-operator flag (god-mode). Drives the Platform sidebar group. */
  isOperator?: boolean;
}
export interface Plan {
  // bonusCredits is included in the header credit total so the chip
  // reads "monthly remainder + bonus" not "monthly remainder" alone.
  name: string;
  renews: string;
  creditsTotal: number;
  creditsUsed: number;
  bonusCredits?: number;
  // Visualizer renders cost 0 credits, so the credit balance above says nothing
  // about them. They are bounded by GPU SECONDS instead. Surfaced so the UI can
  // show what is left rather than let a button labelled "free" return a 402.
  visualizerSecondsTotal?: number;
  visualizerSecondsUsed?: number;
}

/* ---------- Dashboard ---------- */
export interface DashboardStats {
  credits: number;
  creditsPercent: number;
  monthlyQuota?: number;
  monthlyUsed?: number;
  bonusCredits?: number;
  generations: number;
  generationsDelta: number;
  inQueue: number;
  queueRendering: number;
  queueQueued: number;
  published: number;
  publishedChannels: number;
}
export interface ActiveGeneration {
  state: DotState;
  title: string;
  type: string;
  model: string;
  progress: number;
  time: string;
}
export interface CreditSegment {
  label: string;
  value: number;
  color: string;
}
export interface ActivityItem {
  state: DotState;
  text: string;
  ago: string;
}
export interface DashboardData {
  user: User;
  stats: DashboardStats;
  active: ActiveGeneration[];
  creditBreakdown: CreditSegment[];
  channels: ChannelSummary[];
  activity: ActivityItem[];
  recent: RecentWorkItem[];
}
export interface RecentWorkItem {
  /** Render id — populated for real jobs, null for stub fixtures. */
  id?: string;
  title: string;
  kind: MediaKind;
  meta: string;
  palette: string;
  status: DotState;
  signature?: string;
  count?: number;
  // Parity with LibraryItem so the dashboard's Recent Work strip can
  // render the same active card (real cover art, real waveform,
  // click-to-open detail sheet, kebab menu).
  genre?: string;
  provider?: string;
  duration?: string;
  coverImageUrl?: string;
  waveform?: number[];
}

/* ---------- Library ---------- */
export interface LibraryItem {
  id: string;
  projectId?: string;
  title: string;
  kind: MediaKind;
  type: MediaKind;
  aspect: Aspect;
  duration: string;
  size: string;
  model?: string;
  imageModel?: string;
  videoModel?: string;
  status: string;
  created: string;
  source?: string;
  format?: string;
  signature?: string;
  palette: string;
  publish: PublishState;
  componentIds?: string[];
  videoCount?: number;
  sourceVideoIds?: string[];
  // Card metadata: optional because legacy/CLI-produced jobs may not
  // have run through the orchestrator.
  genre?: string;
  provider?: string;
  // Consumer-facing tier (Amateur/Pro/Studio/Legend) — shown on the
  // card metadata row in place of the engine name. tier is the key
  // ("legend"); tierLabel is the display string ("Legend").
  tier?: string;
  tierLabel?: string;
  // Cover-art URL — set when Art Director produced one for this item.
  coverImageUrl?: string;
  // Pre-computed peak buckets (~80 floats in [0,1]) for the music
  // waveform thumbnail. Renders as SVG bars in the card.
  waveform?: number[];
  // Remix lineage — set when this render was created as a regen of
  // another. ``remixOfId`` points at the source job; ``remixKind`` is
  // a short tag ("cover_regen" | "viz_regen" | "motion_regen" |
  // "music_remix"). The library card uses both to render a small
  // "remix of …" chip below the title.
  remixOfId?: string;
  remixKind?: string;
  // True when this music item was uploaded by the user. Drives the
  // "AI Remix" action (Suno upload-cover / add-instrumental).
  uploaded?: boolean;
  // Direct R2-CDN URL of the primary artifact (video/visualizer/bundle).
  // Used for the hover-play <video>.
  mediaUrl?: string;
  // Direct R2-CDN URL of a small JPG poster frame. The library shows
  // this as the still thumbnail for video items (instant ~30KB image)
  // instead of loading the multi-MB video.
  posterUrl?: string;
}
export interface PublishRequest {
  channels: string[];
  visibility: "public" | "unlisted" | "private";
  scheduleAt?: string;
  hideAlreadyPublished?: boolean;
}

/* ---------- Renders ---------- */
export type RenderTab = "active" | "complete" | "failed";
export interface RenderJob {
  id: string;
  state: DotState;
  title: string;
  type: string;
  model: string;
  progress: number;
  elapsed: string;
  cost: number;
  tab: RenderTab;
  /** Aspect ratio the job was rendered at ("16:9" | "9:16" | "1:1"). */
  aspect?: string;
  /** Music render id this row was derived from — set on image / video /
   * visualizer rows that the user kicked off from a music render's
   * approval flow. Used by the Visualizer's library picker to show
   * only motion clips made for the current song. */
  parentMusicRenderId?: string;
}
export interface RenderDetailVersion {
  id: string;
  label: string;
  created: string;
  duration: string;
}
export interface RenderBrief {
  title?: string;
  genre?: string;
  subgenre?: string;
  mood?: string;
  style?: string;
  bpm?: number;
  key?: string;
  songStructure?: string;
  instrumentation?: string[];
  hasVocals?: boolean;
  lyricsBody?: string;
  hookLine?: string;
  language?: string;
}
export type BriefStatus =
  | "applied"
  | "skipped:disabled"
  | "skipped:no-api-key"
  | "skipped:error"
  | "skipped:import-failed"
  | "skipped:config-failed"
  | "not-attempted";
export interface RenderPanelCandidate {
  provider: string;
  score?: number;
  verdict?: string;
  isWinner: boolean;
  reviewStatus?: string;
  role?: string;
}
export interface RenderDetail {
  id: string;
  title: string;
  kind: MediaKind;
  state: DotState;
  progress: number;
  elapsed: string;
  cost: number;
  model?: string;
  provider?: string;
  prompt?: string;
  created: string;
  components: string[];
  versions: RenderDetailVersion[];
  publish?: PublishState;
  brief?: RenderBrief;
  briefStatus?: BriefStatus;
  briefError?: string;
  instrumental?: boolean;
  panel?: RenderPanelCandidate[];
  compositeStatus?: string;
  reference?: string;
  events?: RenderEvent[];
  error?: string;
  contentViolation?: boolean;
  /** Video stage only. "ok" | "failed" | "skipped". When "failed", the
   *  runner couldn't mux the user's music into the motion clip and
   *  shipped silent video — the SPA surfaces a sticky warning so the
   *  user knows before they share the file. `audioMuxError` carries
   *  the underlying reason when status is "failed". */
  audioMux?: string;
  audioMuxError?: string;
}
export type RenderEventStatus = "start" | "complete" | "skipped" | "error";
export interface RenderEvent {
  stage: string;
  status: RenderEventStatus | string;
  ts: string;
  detail?: Record<string, unknown> | null;
}
export interface RenderActionResponse {
  jobId: string;
  state: DotState;
  message: string;
  url?: string;
}
export interface RenderProgressFrame {
  jobId: string;
  state: DotState;
  progress: number;
  elapsed: string;
}

/* ---------- Channels ---------- */
export interface ChannelSummary {
  platform: string;
  handle: string;
  state: DotState;
  mark?: string;
  bg?: string;
}
export interface Channel {
  /** Backend gives id when this is a real DB row; mock fixtures don't. */
  id?: string;
  platform: string;
  handle: string;
  externalAccountId?: string;
  /** Chrome / mock-only stats — not present on the live tenant_channels feed. */
  subs?: string;
  last?: string;
  scheduled?: number;
  state: DotState;
  createdAt?: string;
  lastUsedAt?: string | null;
}
export interface ChannelConnectResponse {
  platform: string;
  authorizeUrl: string;
  state: string;
}

/* ---------- Generate ---------- */
export interface ModelOption {
  id: string;
  label: string;
  kind: "music" | "image" | "video";
  creditsPerGeneration: number;
}

export interface TierMappingCell {
  kind: "music" | "image" | "video";
  tier: "amateur" | "pro" | "studio" | "legend";
  modelId: string;
  modelLabel: string;
  creditsPerGeneration: number;
  isDefault: boolean;
  defaultModelId: string;
}

export interface TierMappingsView {
  cells: TierMappingCell[];
  availableModels: Record<"music" | "image" | "video", ModelOption[]>;
}
export interface PlatformFormatOption {
  key: string;
  label: string;
  aspect: string;
  width: number;
  height: number;
  platforms: string[];
}
export interface TierOption {
  key: string;
  label: string;
  description: string;
  modelId: string;
  creditsPerGeneration: number;
}
export interface TiersByKind {
  music: TierOption[];
  image: TierOption[];
  video: TierOption[];
}
export interface GenerateConfig {
  musicModels: ModelOption[];
  imageModels: ModelOption[];
  videoModels: ModelOption[];
  tiers: TiersByKind;
  platformFormats: PlatformFormatOption[];
}
export type GenerateStageType = "music" | "image" | "video" | "visualizer";
export interface GenerateRequest {
  projectName: string;
  stage: GenerateStageType;
  prompt: string;
  // Either ``tier`` (consumer-facing) OR ``modelId`` (operator
  // override). The BFF prefers tier when both are sent.
  modelId?: string;
  tier?: string;
  generations: number;
  reference?: string;
  /**
   * Pre-generation review flow output. When set, the BFF bakes these
   * edited values into job.params and the runner skips the in-render
   * orchestrator pass. All fields optional so the SPA can ship partial
   * edits (e.g. only the lyrics).
   */
  prebuiltBrief?: {
    title?: string;
    genre?: string;
    mood?: string;
    style?: string;
    bpm?: number;
    key?: string;
    instrumentation?: string[];
    hasVocals?: boolean;
    lyricsBody?: string;
    language?: string;
  };
  // Image-stage only: when the SPA reached this lane from an
  // approved music render's "Image" hand-off, pass that music id so
  // the resulting image auto-links as the music card's cover thumb.
  parentRenderId?: string;
  // Video-stage only: image render id used for first-frame
  // conditioning (the runner pulls bytes via storage and passes as
  // the i2v model's start image).
  parentImageRenderId?: string;
  // Video-stage only: music render id muxed in as the video's audio
  // track. Without it the video ships with the i2v model's default
  // audio (usually silent).
  parentMusicRenderId?: string;
  // Image-stage only: which platform formats to render alongside the
  // primary image (PLATFORM_FORMATS keys — yt_thumb, yt_short,
  // ig_square, tiktok, etc.). Each becomes a sibling artifact with
  // a downloadable URL the detail sheet exposes.
  platformFormats?: string[];
  // Image + video only: the "Built with Vocaler" badge ships by default.
  // Set true to PAY the flat removal fee (10 cr) and strip it.
  removeWatermark?: boolean;
  // Internal-only: marks a render as a composition input (e.g. the visualizer
  // motion sub-render). The runner skips the default watermark and the BFF
  // charges no removal fee — it's never shown to the user on its own.
  compositionInput?: boolean;
  // Visualizer stage only: an MV engine config produced by the embedded MV
  // editor (the embed bridge's mv.embed.state payload, 'mv-legacy-config/1').
  // The BFF dispatches it to the GPU farm like an MV render — the daemon
  // replays this exact design.
  mvState?: Record<string, unknown>;
  // Music stage: the user's own lyrics. When set, used verbatim (the
  // orchestrator still derives the style/title). Blank = AI writes the lyrics.
  lyrics?: string;
  // Video-stage: forces the i2v engine to render at this aspect so
  // motion video matches the visualizer canvas (visualizer composites
  // the motion as its background — mismatch means cropped/letterboxed
  // playback). 1:1 / 16:9 / 9:16. IMAGE-stage also accepts it now: the
  // chosen release aspect from the RemixSetupPanel steers the primary
  // cover shape.
  aspect?: string;
  // Image-stage release metadata captured in the RemixSetupPanel.
  // ``artist`` is the artist/act name; ``releaseTitle`` is the release
  // title. Both optional — a normal submit without them is unchanged.
  artist?: string;
  releaseTitle?: string;
  /**
   * Visualizer-stage only — front-end-derived visual config the
   * server-side composer uses to skin the output: title + subtitle
   * text overlay, accent colour for the audio-reactive wave. Loose
   * dict so the SPA can evolve without a schema dance.
   */
  visualizerConfig?: {
    subtitle?: string;
    accent?: string;
  };
  /**
   * Visualizer-stage only: full composer state snapshot from
   * ``VisualizerLane``. When present the BFF queues a Chromium-mode
   * render on Lambda; the same engine TypeScript runs there and
   * produces pixel-equivalent output to the browser preview. Loose
   * dict so the SPA can evolve without a schema dance — Lambda reads
   * only the keys the bundled engine version understands.
   */
  composerState?: Record<string, unknown>;
  /**
   * Visualizer-stage only: how many seconds of the music to render.
   * Defaults server-side to the music's full duration when omitted.
   * Capped at 600s on the BFF side.
   */
  durationS?: number;
  /**
   * Visualizer-stage HYBRID mode: motion footage as the full-frame
   * background + an audio-reactive strip in the bottom third.
   * ``hybrid`` flags the mode; ``hybridMotionRenderId`` is the parent
   * motion-video render whose clip loops as the background.
   */
  hybrid?: boolean;
  hybridMotionRenderId?: string;
  /**
   * Music AI-remix of an uploaded track. ``remixMode`` selects the
   * Suno flow ("cover" re-styles, "add_instrumental" layers an
   * instrumental). ``rightsAttestation`` MUST be true — the user
   * affirms they own / have rights to the uploaded audio. The BFF
   * rejects the request without it.
   */
  remixMode?: "cover" | "add_instrumental";
  rightsAttestation?: boolean;
  // Remix lineage — set when this submission is a regen of an
  // existing render. The BFF stamps params.remix_of_render_id /
  // remix_kind and the library card renders a "remix of …" chip.
  // Independent of parentRenderId (cover linkage).
  remixOfRenderId?: string;
  remixKind?: string;
}
export interface GenerateResponse {
  jobId: string;
  stage: GenerateStageType;
  creditsCharged: number;
  state: DotState;
}
/**
 * Result of POST /tracks/{jobId}/analyze. ``bpm`` / ``key`` /
 * ``durationS`` are MEASURED (librosa) and may be null when the analyzer
 * couldn't determine them; ``genre`` / ``mood`` are an optional AI
 * SUGGESTION (null → the user types their own). ``cached`` is true when
 * the BFF served a memoized analysis instead of re-running librosa.
 */
export interface TrackAnalysis {
  bpm: number | null;
  key: string | null;
  durationS: number | null;
  genre: string | null;
  mood: string | null;
  cached: boolean;
}
export interface GenerateBatchRowInput {
  name: string;
  prompt: string;
  model?: string;
  aspect?: string;
}
export interface GenerateBatchRequest {
  rows: GenerateBatchRowInput[];
  validOnly: boolean;
}
export interface GenerateBatchRowResult {
  index: number;
  name: string;
  valid: boolean;
  issue?: string;
  jobId?: string;
}
export interface GenerateBatchResponse {
  queued: number;
  creditsCharged: number;
  jobIds: string[];
  rows: GenerateBatchRowResult[];
}

/* ---------- Credits (spend-confirmation ledger) ---------- */
// Mirrors the BFF credits contract at /api/v1/web/credits/*. Decimal
// amounts come over the wire as strings to avoid float drift; the SPA
// only ever displays them (Number() for the insufficient comparison).
export interface CreditBalance {
  balance: string;
  ledgerEnabled: boolean;
}
/*
 * No CreditEstimate types: pricing is not an API surface. Every credit
 * price the UI shows comes from /generate/config
 * (`ModelOption.creditsPerGeneration`), which is the same number the
 * backend charges. The old /credits/estimate endpoint priced against a
 * separate `price_catalog` table that disagreed with it by ~14.5x.
 */

/* ---------- Bundles ---------- */
export interface BundleRequest {
  name: string;
  videoIds: string[];
  transition: "Cut" | "Fade" | "Cross" | "Black hold";
  audio?: string;
  chapters: boolean;
}

/* ---------- Settings ---------- */
export interface WebhookRow {
  url: string;
  events: string;
  status: string;
  lastTriggered: string;
}
export interface ApiKeyRow {
  label: string;
  prefix: string;
  created: string;
  lastUsed: string;
}

/* ---------- Command palette ---------- */
export interface Command {
  id: string;
  label: string;
  group: string;
  icon: string;
  hint?: string;
}

/* ---------- Integrations ---------- */
export type ProviderKind = "music" | "image" | "video" | "orchestrator" | "storage";
export interface ProviderExtraField {
  envVar: string;
  label: string;
  placeholder: string;
  helpText: string;
  required: boolean;
  /** Current persisted value (plaintext — these are not secrets). */
  value: string | null;
}
export interface ProviderRow {
  id: string;
  label: string;
  kind: ProviderKind;
  configured: boolean;
  enabled: boolean;
  modelCount: number;
  envVar: string;
  envVarAliases: string[];
  /** Empty string when the provider only needs a single key. */
  secondaryEnvVar: string;
  /** Overrides the default "API key" label on the primary input. */
  primaryLabel: string;
  /** Label for the secondary input (e.g. "Secret Access Key"). */
  secondaryLabel: string;
  /** Whether the secondary value is already persisted. */
  secondaryConfigured: boolean;
  docsUrl: string;
  creditsAvailableUsd: number | null;
  creditsUsedPeriodUsd: number;
  /** Provider prepaid balance (e.g. Suno credits) as a display string, or
   *  null for pay-as-you-go providers. ``balanceLow`` flags a near-empty one. */
  balanceLabel?: string | null;
  balanceLow?: boolean;
  lastUsedAt: string | null;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  /** Last 4 chars of the stored API key, for "•••• abcd" display. Null until saved. */
  last4: string | null;
  defaultModels: ModelOption[];
  /** Extra non-secret config fields (e.g. bucket name, region). */
  extraFields: ProviderExtraField[];
}
export interface TestResult {
  ok: boolean;
  latencyMs: number;
  message: string;
  probed?: { models?: string[]; balanceUsd?: number };
}
export interface SaveProviderInput {
  apiKey: string;
  enabled?: boolean;
  options?: Record<string, unknown>;
  /** Secondary value for HMAC-style providers (AWS secret, Kling secret). */
  secondary?: string;
  /** env_var → value for the provider's extra fields. */
  extraValues?: Record<string, string>;
}

/* ---------- Team & access ---------- */
export type MemberStatus = "active" | "invited" | "suspended";
export interface TeamMember {
  userId: string;
  email: string;
  name: string | null;
  role: Role;
  status: MemberStatus;
  emailVerifiedAt: string | null;
  lastSeenAt: string | null;
  isSelf: boolean;
}
export interface InviteResult {
  inviteId: string;
  inviteUrl: string;
}

/* ---------- API envelope ---------- */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number = 500,
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/* ---------- Platform admin (god-mode) ---------- */
export interface PlatformStats {
  tenants: { total: number; active: number; suspended: number; newThisMonth: number };
  users: { total: number; operators: number; active: number; suspended: number };
  jobs: { total: number; active: number; completed: number; failed: number; last24h: number };
  revenue: { creditsChargedThisMonth: number; estimatedUsdThisMonth: number | null };
  topProviders: { id: string; label: string; jobsLast30d: number; creditsLast30d: number }[];
}
export interface PlatformTenantRow {
  tenantId: string;
  name: string;
  plan: string;
  status: "active" | "suspended" | "delinquent";
  memberCount: number;
  ownerEmails: string[];
  monthlyRenderQuota: number;
  monthlyRendersUsed: number;
  bonusCredits: number;
  createdAt: string;
  lastActiveAt: string | null;
}
export interface PlatformTenantDetail extends PlatformTenantRow {
  members: { userId: string; email: string; role: string; status: string }[];
  recentJobs: PlatformJobRow[];
  recentEvents: { ts: string; kind: string; message: string }[];
  integrations: { providerId: string; configured: boolean; lastTestOk: boolean | null }[];
  billing: {
    stripeCustomerId: string | null;
    subscriptionStatus: string | null;
    currentPeriodEnd: string | null;
    paymentFailureCount: number;
  };
}
export interface PlatformTenantPatch {
  status?: "active" | "suspended";
  plan?: string;
  monthlyRenderQuota?: number;
}
export interface PlatformUserRow {
  userId: string;
  email: string;
  isOperator: boolean;
  status: "active" | "suspended" | "unverified";
  tenantCount: number;
  createdAt: string;
  lastSeenAt: string | null;
}
export interface PlatformUserDetail extends PlatformUserRow {
  memberships: {
    tenantId: string;
    tenantName: string;
    role: string;
    status: string;
    invitedAt: string | null;
    joinedAt: string;
  }[];
  recentActivity: { ts: string; action: string; detail: string | null }[];
}
export interface PlatformUserPatch {
  isOperator?: boolean;
  status?: "active" | "suspended";
}
export interface PlatformJobRow {
  jobId: string;
  tenantId: string;
  tenantName: string;
  kind: "music" | "image" | "video" | "bundle";
  model: string | null;
  status: string;
  progress: number;
  creditsCharged: number;
  ownerEmail: string | null;
  queuedAt: string;
  finishedAt: string | null;
  errorShort: string | null;
}
export interface PlatformJobDetail extends PlatformJobRow {
  params: Record<string, unknown>;
  events: { ts: string; status: string; stage: string | null; detail: string | null }[];
  artifacts: { id: string; kind: string; sizeBytes: number; storageKey: string }[];
  costEvents: { ts: string; providerId: string; usdMicros: number; label: string }[];
}
export interface AuditEvent {
  id: string;
  ts: string;
  actorEmail: string | null;
  actorIsOperator: boolean;
  target: { kind: "tenant" | "user" | "job" | "integration" | "membership" | "webhook"; id: string; label: string | null };
  action: string;
  detail: string | null;
}
/** Spec: /platform/lookups/tenants returns this shape (not the generic id/label pair). */
export interface PlatformTenantLookup {
  tenantId: string;
  name: string;
}
/** Generic id/label option kept for callers that built against the original shape. */
export interface PlatformLookupOption {
  id: string;
  label: string;
}
export interface PlatformQuery {
  /** Spec uses `search`; `q` retained as a legacy alias. */
  search?: string;
  q?: string;
  status?: string;
  plan?: string;
  type?: string;
  tenantId?: string;
  kind?: string;
  model?: string;
  actor?: string;
  targetKind?: string;
  action?: string;
  page?: number;
  pageSize?: number;
}

// ---------------- Platform Settings ----------------
export interface PlatformOAuthProviderState {
  provider: string;
  label: string;
  clientId: string | null;
  clientSecretMasked: string | null;
  configured: boolean;
}
export interface PlatformSmtpState {
  host: string | null;
  port: string | null;
  username: string | null;
  passwordMasked: string | null;
  fromAddr: string | null;
  useTls: string | null;
  useSsl: string | null;
  configured: boolean;
}
export interface PlatformStripeState {
  publishableKey: string | null;
  secretKeyMasked: string | null;
  webhookSecretMasked: string | null;
  configured: boolean;
}
export interface PlatformCloudflareState {
  apiTokenMasked: string | null;
  accountId: string | null;
  tunnelId: string | null;
  configured: boolean;
}
export interface PlatformGithubState {
  webhookSecretMasked: string | null;
  repo: string | null;
  configured: boolean;
}
export interface PlatformOriginsState {
  appBaseUrl: string | null;
  callbackBase: string | null;
}
export interface PlatformSettings {
  oauth: { providers: PlatformOAuthProviderState[] };
  smtp: PlatformSmtpState;
  stripe: PlatformStripeState;
  cloudflare: PlatformCloudflareState;
  github: PlatformGithubState;
  origins: PlatformOriginsState;
}
export interface PlatformSmtpWrite {
  host: string;
  port?: string;
  username?: string;
  password?: string; // optional — leave undefined to keep existing
  fromAddr: string;
  useTls?: string;
  useSsl?: string;
}
export interface PlatformStripeWrite {
  publishableKey: string;
  secretKey?: string;
  webhookSecret?: string;
}
export interface PlatformCloudflareWrite {
  apiToken?: string;
  accountId: string;
  tunnelId?: string;
}
export interface PlatformGithubWrite {
  webhookSecret?: string;
  repo?: string;
}
export interface PlatformOriginsWrite {
  appBaseUrl?: string;
  callbackBase?: string;
}
